import { APPROVALS_URL } from '../constants';
import { apiSlice, ORDER_LIST_TAG } from './apiSlice';

const approvalListParams = ({ page = 1, pageSize = 15, q, kind } = {}) => ({
	page,
	pageSize,
	...(q ? { q } : {}),
	...(kind ? { kind } : {}),
});

export const approvalsApiSlice = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		initiateQuotaApproval: builder.mutation({
			query: (approval) => ({
				url: `${APPROVALS_URL}/quota`,
				method: 'POST',
				body: { ...approval },
			}),
			invalidatesTags: ['Approval'],
		}),
		initiateCustomerCreateApproval: builder.mutation({
			query: (approval) => ({
				url: `${APPROVALS_URL}/customer`,
				method: 'POST',
				body: { ...approval },
			}),
			invalidatesTags: ['Approval'],
		}),
		initiateOrderApproval: builder.mutation({
			query: (approval) => ({
				url: `${APPROVALS_URL}/order`,
				method: 'POST',
				body: { ...approval },
			}),
			invalidatesTags: ['Approval'],
		}),
		getMyCustomerApprovals: builder.query({
			query: (args) => ({
				url: `${APPROVALS_URL}/mine/customer`,
				params: approvalListParams(args),
			}),
			providesTags: ['Approval'],
			keepUnusedDataFor: 5,
		}),
		getMyOrderApprovals: builder.query({
			query: (args) => ({
				url: `${APPROVALS_URL}/mine/order`,
				params: approvalListParams(args),
			}),
			providesTags: ['Approval'],
			keepUnusedDataFor: 5,
		}),
		getAllOrderApprovals: builder.query({
			query: (args) => ({
				url: `${APPROVALS_URL}/all/order`,
				params: approvalListParams(args),
			}),
			providesTags: ['Approval'],
			keepUnusedDataFor: 5,
		}),
		getAllCustomerApprovals: builder.query({
			query: (args) => ({
				url: `${APPROVALS_URL}/all/customer`,
				params: approvalListParams(args),
			}),
			providesTags: ['Approval'],
			keepUnusedDataFor: 5,
		}),
		getAllQuotaApprovals: builder.query({
			query: (args) => ({
				url: `${APPROVALS_URL}/all/quota`,
				params: approvalListParams(args),
			}),
			providesTags: ['Approval'],
			keepUnusedDataFor: 5,
		}),
		getMyOrderApprovalPendings: builder.query({
			query: () => ({
				url: `${APPROVALS_URL}/mine/pendings`,
			}),
			providesTags: ['Approval'],
			keepUnusedDataFor: 5,
		}),
		getCustomerApprovalPendings: builder.query({
			query: () => ({
				url: `${APPROVALS_URL}/pendings/customer`,
			}),
			providesTags: ['Approval'],
			keepUnusedDataFor: 5,
		}),
		getQuotaApprovalPendings: builder.query({
			query: () => ({
				url: `${APPROVALS_URL}/pendings/quota`,
			}),
			providesTags: ['Approval'],
			keepUnusedDataFor: 5,
		}),
		getFinalCostApprovalPendings: builder.query({
			query: () => ({
				url: `${APPROVALS_URL}/pendings/final-cost`,
			}),
			providesTags: ['Approval'],
			keepUnusedDataFor: 5,
		}),
		getCustomerApplyingApprovals: builder.query({
			query: () => ({
				url: `${APPROVALS_URL}/applying/customer`,
			}),
			providesTags: ['Approval'],
			keepUnusedDataFor: 5,
		}),
		getFinalCostRejectedApprovals: builder.query({
			query: () => ({
				url: `${APPROVALS_URL}/rejected/final-cost`,
			}),
			providesTags: ['Approval'],
			keepUnusedDataFor: 5,
		}),
		getRejectedCustomerApprovals: builder.query({
			query: ({ scope = 'mine' } = {}) => ({
				url: `${APPROVALS_URL}/rejected/customer`,
				params: { scope },
			}),
			providesTags: ['Approval'],
			keepUnusedDataFor: 5,
		}),
		getRejectedQuotaApprovals: builder.query({
			query: ({ scope = 'mine' } = {}) => ({
				url: `${APPROVALS_URL}/rejected/quota`,
				params: { scope },
			}),
			providesTags: ['Approval'],
			keepUnusedDataFor: 5,
		}),
		getFinalCostApplyingApprovals: builder.query({
			query: () => ({
				url: `${APPROVALS_URL}/applying/final-cost`,
			}),
			providesTags: ['Approval'],
			keepUnusedDataFor: 5,
		}),
		getQuotaApplyingApprovals: builder.query({
			query: () => ({
				url: `${APPROVALS_URL}/applying/quota`,
			}),
			providesTags: ['Approval'],
			keepUnusedDataFor: 5,
		}),
		getMyQuotaApprovals: builder.query({
			query: (args) => ({
				url: `${APPROVALS_URL}/mine/quota`,
				params: approvalListParams(args),
			}),
			providesTags: ['Approval'],
			keepUnusedDataFor: 5,
		}),
		getApprovalById: builder.query({
			query: (approvalId) => ({
				url: `${APPROVALS_URL}/${approvalId}`,
			}),
			providesTags: ['Approval'],
			keepUnusedDataFor: 5,
		}),
		getCustomerApprovalHistory: builder.query({
			query: (approvalId) => ({
				url: `${APPROVALS_URL}/${approvalId}/history`,
			}),
			providesTags: ['Approval'],
			keepUnusedDataFor: 5,
		}),
		updateCustomerApprovalById: builder.mutation({
			query: (data) => ({
				url: `${APPROVALS_URL}/${data._id}/customer`,
				method: 'PUT',
				body: data,
			}),
			invalidatesTags: ['Approval'],
		}),
		updateOrderApprovalById: builder.mutation({
			query: (data) => ({
				url: `${APPROVALS_URL}/${data._id}/order`,
				method: 'PUT',
				body: data,
			}),
			invalidatesTags: ['Approval', 'Order'],
		}),
		deleteApproval: builder.mutation({
			query: (approvalId) => ({
				url: `${APPROVALS_URL}/${approvalId}`,
				method: 'DELETE',
			}),
			invalidatesTags: ['Approval'],
		}),
		cancelCustomerApplication: builder.mutation({
			query: (approvalId) => ({
				url: `${APPROVALS_URL}/${approvalId}/cancel`,
				method: 'PUT',
			}),
			invalidatesTags: ['Approval'],
		}),
		withdrawApprovalApplication: builder.mutation({
			query: (approvalId) => ({
				url: `${APPROVALS_URL}/${approvalId}/withdraw`,
				method: 'PUT',
			}),
			invalidatesTags: ['Approval', 'Notification', ORDER_LIST_TAG],
		}),
	}),
});

export const {
	useInitiateQuotaApprovalMutation,
	useInitiateCustomerCreateApprovalMutation,
	useInitiateOrderApprovalMutation,
	useGetMyCustomerApprovalsQuery,
	useGetMyOrderApprovalsQuery,
	useGetAllOrderApprovalsQuery,
	useGetAllCustomerApprovalsQuery,
	useGetAllQuotaApprovalsQuery,
	useGetMyQuotaApprovalsQuery,
	useGetApprovalByIdQuery,
	useGetCustomerApprovalHistoryQuery,
	useUpdateCustomerApprovalByIdMutation,
	useUpdateOrderApprovalByIdMutation,
	useDeleteApprovalMutation,
	useCancelCustomerApplicationMutation,
	useWithdrawApprovalApplicationMutation,
	useGetMyOrderApprovalPendingsQuery,
	useGetCustomerApprovalPendingsQuery,
	useGetQuotaApprovalPendingsQuery,
	useGetFinalCostApprovalPendingsQuery,
	useGetCustomerApplyingApprovalsQuery,
	useGetFinalCostApplyingApprovalsQuery,
	useGetFinalCostRejectedApprovalsQuery,
	useGetRejectedCustomerApprovalsQuery,
	useGetRejectedQuotaApprovalsQuery,
	useGetQuotaApplyingApprovalsQuery,
} = approvalsApiSlice;
